'use client';

import type { ReactNode } from 'react';
import {
  // eslint-disable-next-line no-restricted-imports
  Button as MuiButton,
  // eslint-disable-next-line no-restricted-imports
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { buttonClasses } from '@mui/material/Button';

import type { WatershedLinkProps } from '../types/LinkProps';
import createWatershedButton from '../utils/createWatershedButton';
import type { MuiTooltipProps } from '../utils/MaybeTooltip';

/**
 * `ButtonProps` is a union of `MuiButtonProps` and `WatershedLinkProps` as
 * `<Button>` automatically renders either a link or a button based on the `href`
 * prop.
 *
 * If you're looking to show a loading state on the button, use
 * `<ProgressButton>` instead.
 */
export type ButtonProps = Omit<
  MuiButtonProps,
  | 'components'
  | 'componentsProps'
  | 'to'
  | 'href'
  // We're not using the `tw` experimental Tailwind prop, which is marked as
  // optional in `MuiButtonProps`. But for some reason, when we introduced
  // `next-env.d.ts` to `ui-core`, it started causing this bizarre type error,
  // so we'll just omit it here.
  // More details:
  // https://github.com/watershed-climate/ghg/pull/40050#issuecomment-1673322341
  | 'tw'
> &
  // link props makes children required whereas button props makes children optional
  Omit<WatershedLinkProps, 'onClick' | 'children'> & {
    tooltip?: ReactNode;
    tooltipProps?: Partial<MuiTooltipProps>;
    tooltipPlacement?: MuiTooltipProps['placement'];
    isIcon?: boolean;
  };
const Button = createWatershedButton<ButtonProps>(MuiButton);
export default Button;

export { buttonClasses };
